/**
 * Phase 6-γ.4 — DLQ Admin Panel (owner-only).
 * Calls GET /_admin/delivery-queue/dead?group_by=connection_error +
 * POST /_admin/delivery-queue/tick + POST /_admin/delivery-queue/{id}/requeue.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, RefreshCcw, ChevronDown, ChevronRight, Play } from "lucide-react";
import { api, auth } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

export default function DeadLetterQueue() {
  const t = useT();
  const user = auth.getUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [tickResult, setTickResult] = useState(null);
  const [requeueBusy, setRequeueBusy] = useState({});
  const [viewMode, setViewMode] = useState("grouped");

  const isOwner = (user?.role === "owner" || user?.role === "admin");

  const load = () => {
    setLoading(true);
    const params = viewMode === "grouped"
      ? { group_by: "connection_error" } : {};
    api.get("/integrations/crm/_admin/delivery-queue/dead", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(viewMode === "grouped"
        ? { groups: [], total_groups: 0, total_dead: 0 }
        : { items: [], total: 0, page: 1, limit: 50 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (isOwner) load(); }, [isOwner, viewMode]);

  if (!isOwner) {
    return (
      <div className="p-6 max-w-xl" data-testid="dlq-forbidden">
        <ShieldAlert className="h-10 w-10 text-amber-500 mb-3" />
        <h1 className="text-2xl font-semibold">
          {t("dlq.forbidden_title", "Owner only")}
        </h1>
        <p className="text-sm text-muted-foreground mt-2">
          {t("dlq.forbidden_message", "Dead-letter queue requires system.admin.")}
        </p>
        <Link to="/app/cockpit" className="underline mt-4 inline-block text-sm">
          {t("common.back", "Zurück zum Cockpit")}
        </Link>
      </div>
    );
  }

  const runTick = async () => {
    try {
      const r = await api.post("/integrations/crm/_admin/delivery-queue/tick",
                                  { limit: 50 });
      setTickResult(r.data);
      toast.success(`Tick: ${r.data.delivered} delivered, ${r.data.dead} dead`);
      load();
    } catch {
      toast.error("Tick failed");
    }
  };

  const requeueGroup = async (group) => {
    setRequeueBusy((s) => ({ ...s, [group.connection_id]: true }));
    let done = 0;
    for (const did of group.delivery_ids) {
      try {
        await api.post(
          `/integrations/crm/_admin/delivery-queue/${did}/requeue`);
        done += 1;
      } catch (e) { /* swallow per-id; refresh at end */ }
    }
    setRequeueBusy((s) => ({ ...s, [group.connection_id]: false }));
    toast.success(`Requeued ${done}/${group.delivery_ids.length}`);
    load();
  };

  return (
    <div className="p-6 space-y-6" data-testid="dlq-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("dlq.title", "Dead-Letter-Queue")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("dlq.subtitle", "Fehlgeschlagene Webhook-Lieferungen.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border p-0.5 bg-muted/30"
                  data-testid="dlq-view-toggle">
            <button
              type="button"
              onClick={() => setViewMode("grouped")}
              className={`px-3 py-1 text-xs rounded ${viewMode === "grouped" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
              data-testid="dlq-view-grouped"
            >
              {t("dlq.toggle_grouped", "Gruppiert")}
            </button>
            <button
              type="button"
              onClick={() => setViewMode("flat")}
              className={`px-3 py-1 text-xs rounded ${viewMode === "flat" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
              data-testid="dlq-view-flat"
            >
              {t("dlq.toggle_flat", "Flach")}
            </button>
          </div>
          <Button onClick={runTick} data-testid="dlq-run-tick">
            <Play className="h-4 w-4 mr-2" />
            {t("dlq.run_tick", "Tick jetzt ausführen")}
          </Button>
        </div>
      </header>

      {tickResult && (
        <Card data-testid="dlq-tick-result">
          <CardContent className="p-3 text-sm">
            <span className="font-medium">Tick:</span>
            {` ${tickResult.delivered} delivered · ${tickResult.failed_retrying} ${t("dlq.retrying", "Erneut versuchen")} · ${tickResult.dead} dead · ${tickResult.claimed} claimed`}
          </CardContent>
        </Card>
      )}

      {loading && <div data-testid="dlq-loading" className="text-sm text-muted-foreground">…</div>}

      {!loading && viewMode === "grouped" && data && data.total_dead === 0 && (
        <div className="text-sm text-muted-foreground" data-testid="dlq-empty">
          {t("dlq.empty", "Keine fehlgeschlagenen Lieferungen.")}
        </div>
      )}

      {!loading && viewMode === "flat" && data && (data.items || []).length === 0 && (
        <div className="text-sm text-muted-foreground" data-testid="dlq-empty-flat">
          {t("dlq.empty", "Keine fehlgeschlagenen Lieferungen.")}
        </div>
      )}

      {!loading && viewMode === "flat" && data && (data.items || []).length > 0 && (
        <Card data-testid="dlq-flat-list">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground border-b">
                <tr>
                  <th className="p-3">{t("dlq.first_seen", "Erste Sichtung")}</th>
                  <th className="p-3">Connection</th>
                  <th className="p-3">Event</th>
                  <th className="p-3">{t("dlq.attempts", "Versuche")}</th>
                  <th className="p-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((d) => (
                  <tr key={d.id} className="border-b last:border-0"
                       data-testid={`dlq-flat-row-${d.id}`}>
                    <td className="p-3 text-xs">{d.dead_at || "—"}</td>
                    <td className="p-3 text-xs font-mono">{d.connection_id}</td>
                    <td className="p-3 text-xs">{d.event_kind}</td>
                    <td className="p-3 text-xs">{d.attempts}</td>
                    <td className="p-3 text-xs truncate max-w-[300px]"
                         title={d.last_error || ""}>{d.last_error || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {!loading && viewMode === "grouped" && data && data.total_dead > 0 && (
        <div className="space-y-3" data-testid="dlq-groups">
          {data.groups.map((g) => {
            const key = `${g.connection_id}::${g.last_error_signature}`;
            const isOpen = !!expanded[key];
            return (
              <Card key={key} data-testid={`dlq-group-${g.connection_id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between text-base">
                    <button
                      onClick={() => setExpanded((s) => ({ ...s, [key]: !s[key] }))}
                      className="flex items-center gap-2"
                      data-testid={`dlq-group-toggle-${g.connection_id}`}
                    >
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span>{g.connection_name || g.connection_id}</span>
                      <Badge variant="secondary">{g.count}</Badge>
                    </button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => requeueGroup(g)}
                      disabled={!!requeueBusy[g.connection_id]}
                      data-testid={`dlq-requeue-all-${g.connection_id}`}
                    >
                      <RefreshCcw className="h-3 w-3 mr-1" />
                      {t("dlq.requeue_all_in_group", "Alle in Gruppe neu starten")}
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="text-xs text-muted-foreground mb-2">
                    {g.last_error_signature || "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {t("dlq.first_seen", "Erste Sichtung")}: {g.first_seen_at || "—"} ·{" "}
                    {t("dlq.last_seen", "Letzte Sichtung")}: {g.last_seen_at || "—"}
                  </div>
                  {isOpen && (
                    <ul className="mt-3 space-y-1 text-xs"
                         data-testid={`dlq-group-ids-${g.connection_id}`}>
                      {g.delivery_ids.map((did) => (
                        <li key={did} className="font-mono text-muted-foreground">
                          {did}
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
